import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { message } from 'antd'
import { Select, TextInput } from '@forwardflow/ui-v2'
import { businessType, jobPositions } from '@forwardflow/helpers/utils'
import { states } from '@forwardflow/helpers/utils/states'
import useSetupProgress from '@forwardflow/hooks/customHooks/onboarding/useSetupProgress'
import BusinessService from '@forwardflow/services/BusinessService'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { IBusinessDetails } from '@forwardflow/types/onboarding'
import { isOperatorEmail } from '@forwardflow/utils/app.utils'
import { DEMO_BUSINESS, homePath, isFreeFlow, isSandbox, useDevTools } from '../dev/devTools'
import { Card, StepFooter, StepHeader } from '../shell/Card'
import { prettyPhone } from '../shell/format'

type TField = keyof IBusinessDetails
/** The form holds strings only; arrays and flags are converted on submit. */
type TForm = Partial<Record<TField, string>>

const str = (v: unknown): string =>
   typeof v === 'string' ? v : Array.isArray(v) ? v.filter(Boolean).join(', ') : ''

interface IFieldDef {
   name: TField
   label: string
   placeholder?: string
   kind?: 'text' | 'select' | 'email' | 'tel'
   options?: { label: string; value: string }[]
   half?: boolean
   optional?: boolean
   hint?: string
}

const FIELDS: IFieldDef[] = [
   { name: 'legalBusinessName', label: 'Legal business name', placeholder: 'As registered', half: true },
   { name: 'friendlyBusinessName', label: 'Name leads see', placeholder: 'Your brand name', half: true },
   { name: 'websiteUrl', label: 'Website', placeholder: 'https://', half: true },
   { name: 'businessType', label: 'Business type', kind: 'select', options: businessType, half: true },
   { name: 'businessRegistrationNumber', label: 'EIN (9 digits)', placeholder: '12-3456789', half: true },
   {
      name: 'socialMediaProfileUrls',
      label: 'Social profile',
      placeholder: 'https://instagram.com/…',
      half: true,
      optional: true,
      hint: 'Optional. Helps the carriers verify you faster.',
   },
   { name: 'authorizedRepresentativeFirstName', label: 'Your first name', half: true },
   { name: 'authorizedRepresentativeLastName', label: 'Your last name', half: true },
   { name: 'authorizedRepresentativeJobPosition', label: 'Your role', kind: 'select', options: jobPositions, half: true },
   { name: 'email', label: 'Business email', kind: 'email', half: true },
   { name: 'phoneNumber', label: 'Mobile number', kind: 'tel', placeholder: '(719) 555-0100', half: true },
   { name: 'street', label: 'Street address', half: true },
   { name: 'city', label: 'City', half: true },
   { name: 'region', label: 'State', kind: 'select', options: states, half: true },
   { name: 'postalCode', label: 'ZIP', placeholder: '5 digits', half: true },
]

const validate = (name: TField, value: string | undefined, optional?: boolean): string | null => {
   const v = (value || '').trim()
   if (!v) return optional ? null : 'Needed for approval'
   if (name === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Enter a valid email'
   if (name === 'phoneNumber' && v.replace(/\D/g, '').length < 10) return 'Enter a 10-digit US number'
   if (name === 'businessRegistrationNumber' && v.replace(/\D/g, '').length !== 9)
      return 'An EIN has 9 digits'
   if (name === 'postalCode' && !/^\d{5}$/.test(v)) return 'ZIP is 5 digits'
   if ((name === 'websiteUrl' || name === 'socialMediaProfileUrls') && !/^(https?:\/\/)?([\w-]+\.)+[\w-]+/.test(v))
      return 'Enter a web address'
   return null
}

/**
 * Step 7: business details, so texting can be approved.
 *
 * Carriers need to know who is texting (A2P registration). Everything we
 * already know is filled in: saved details, the signed-in user, the owner
 * from Follow Up Boss. Only the blanks are left. Submitting files the
 * registration and finishes setup; calls and voicemails start right away,
 * texts join once the carriers approve. "Do this later" finishes setup too.
 */
export const BusinessDetailsStep = () => {
   const navigate = useNavigate()
   const {
      onboardingData,
      getPreviousStep,
      setBusinessDetails,
      setIsOnBoardingCompleted,
   } = useOnboardingStoreV2()
   const { data: progress } = useSetupProgress()
   const owner = onboardingData.invitedUsers?.find((u) => u.isOwner)

   const prefill = useMemo<TForm>(() => {
      const saved = (progress?.businessDetails || {}) as Partial<IBusinessDetails>
      const record = onboardingData.businessDetails || {}
      const demo = isSandbox() ? DEMO_BUSINESS : {}
      // Later layers win only where they actually say something: a saved
      // record full of empty strings must not blank what the account knows.
      const merged: TForm = {}
      const layers: Array<Record<string, unknown>> = [demo, record, saved]
      layers.forEach((layer) => {
         const keys = Object.keys(layer) as TField[]
         keys.forEach((k) => {
            const v = str(layer[k])
            if (v) merged[k] = v
         })
      })
      const first = (...vals: Array<string | undefined | null>) => vals.find((v) => v && String(v).trim()) || ''
      return {
         ...merged,
         legalBusinessName: first(merged.legalBusinessName),
         friendlyBusinessName: first(merged.friendlyBusinessName, merged.legalBusinessName),
         email: first(merged.email, onboardingData.email, owner?.email),
         phoneNumber: first(merged.phoneNumber, owner?.phone ? prettyPhone(owner.phone) : ''),
         authorizedRepresentativeFirstName: first(merged.authorizedRepresentativeFirstName, owner?.firstName),
         authorizedRepresentativeLastName: first(merged.authorizedRepresentativeLastName, owner?.lastName),
         authorizedRepresentativeEmail: first(merged.authorizedRepresentativeEmail, onboardingData.email, owner?.email),
         authorizedRepresentativeJobPosition: first(merged.authorizedRepresentativeJobPosition, owner?.isOwner ? 'CEO' : ''),
         businessType: first(merged.businessType),
         region: first(merged.region, merged.state),
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [progress?.businessDetails, owner?.id])

   const [values, setValues] = useState<TForm>(prefill)
   const [touched, setTouched] = useState<Set<TField>>(new Set())
   const [submitting, setSubmitting] = useState(false)
   useEffect(() => {
      // Saved details arriving after mount fill the blanks, never overwrite typing.
      setValues((v) => {
         const next: TForm = { ...v }
         ;(Object.keys(prefill) as TField[]).forEach((k) => {
            if (!next[k] && prefill[k]) next[k] = prefill[k]
         })
         return next
      })
   }, [prefill])

   const errors = useMemo(() => {
      const out: Partial<Record<TField, string>> = {}
      FIELDS.forEach((f) => {
         const e = validate(f.name, values[f.name], f.optional)
         if (e) out[f.name] = e
      })
      return out
   }, [values])
   const missing = FIELDS.filter((f) => !f.optional && !values[f.name]?.trim()).length
   const filled = FIELDS.length - FIELDS.filter((f) => !values[f.name]?.trim()).length
   const prefilledCount = FIELDS.filter((f) => prefill[f.name]?.trim()).length
   const isValid = Object.keys(errors).length === 0

   const set = (name: TField, value: string) => {
      setValues((v) => ({ ...v, [name]: value }))
   }
   const touch = (name: TField) => setTouched((t) => new Set(t).add(name))

   const finish = () => {
      if (!isOperatorEmail(onboardingData.email || '')) setIsOnBoardingCompleted(true)
      navigate(homePath())
   }

   const submit = async () => {
      setTouched(new Set(FIELDS.map((f) => f.name)))
      if (!isValid && !isFreeFlow()) return
      setSubmitting(true)
      try {
         const details: IBusinessDetails = {
            ...values,
            socialMediaProfileUrls: values.socialMediaProfileUrls
               ? values.socialMediaProfileUrls.split(',').map((u) => u.trim()).filter(Boolean)
               : undefined,
            hasTeam: undefined,
            logoObjectKey: undefined,
            logoUrl: undefined,
            businessEIIN: values.businessRegistrationNumber,
            state: values.region,
            authorizedRepresentativeEmail: values.authorizedRepresentativeEmail || values.email,
            authorizedRepresentativePhoneNumber: values.phoneNumber,
            businessOwnerName: `${values.authorizedRepresentativeFirstName || ''} ${values.authorizedRepresentativeLastName || ''}`.trim(),
            businessOwnerTitle: values.authorizedRepresentativeJobPosition,
         }
         setBusinessDetails(details)
         if (!isFreeFlow()) {
            await BusinessService.updateBusinessDetails(details as never)
         }
         finish()
      } catch {
         message.error("We couldn't send your details. Check them and try again.")
      } finally {
         setSubmitting(false)
      }
   }

   useDevTools({
      fill: () =>
         setValues((v) => ({
            ...v,
            ...DEMO_BUSINESS,
            businessRegistrationNumber: '12-3456789',
            socialMediaProfileUrls: 'https://instagram.com/patelrealty',
            street: '524 S Tejon St',
            postalCode: '80903',
            region: 'CO',
            authorizedRepresentativeJobPosition: v.authorizedRepresentativeJobPosition || 'CEO',
            businessType: v.businessType || 'Limited Liability',
            phoneNumber: v.phoneNumber || '(719) 555-0100',
         })),
      skip: () => {
         finish()
         return true
      },
   })

   return (
      <div className="space-y-4">
         <Card>
            <StepHeader
               title="Confirm your business details"
               lede="Phone carriers only allow texting from businesses they can identify. We filled in what we already know; complete the rest and we file it for you. Approval takes a few days. Calls and voicemails start today, texts join when it clears."
               right={
                  <span className="text-xs text-white/50 whitespace-nowrap" data-testid="prefill-count">
                     {prefilledCount} of {FIELDS.length} filled in
                  </span>
               }
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-3" data-testid="business-form">
               {FIELDS.map((f) => {
                  const value = values[f.name] || ''
                  const error = touched.has(f.name) ? errors[f.name] : undefined
                  const wasPrefilled = Boolean(prefill[f.name]?.trim())
                  const label = (
                     <span className="flex items-center gap-2">
                        {f.label}
                        {wasPrefilled && <span className="text-[10px] text-white/35 font-normal">from your account</span>}
                        {f.optional && <span className="text-[10px] text-white/35 font-normal">optional</span>}
                     </span>
                  )
                  if (f.kind === 'select') {
                     return (
                        <Select
                           key={f.name}
                           size="sm"
                           label={label}
                           placeholder="Choose"
                           searchable
                           data={f.options || []}
                           value={value || null}
                           onChange={(v) => {
                              set(f.name, v || '')
                              touch(f.name)
                           }}
                           error={error}
                           comboboxProps={{ withinPortal: true }}
                           data-testid={`field-${f.name}`}
                        />
                     )
                  }
                  return (
                     <TextInput
                        key={f.name}
                        size="sm"
                        label={label}
                        description={f.hint}
                        placeholder={f.placeholder}
                        type={f.kind === 'email' ? 'email' : 'text'}
                        inputMode={f.kind === 'tel' ? 'tel' : undefined}
                        value={value}
                        onChange={(e) => set(f.name, e.currentTarget.value)}
                        onBlur={() => touch(f.name)}
                        error={error}
                        data-testid={`field-${f.name}`}
                     />
                  )
               })}
            </div>
         </Card>

         <StepFooter
            onBack={() => getPreviousStep()}
            action={
               missing > 0 && !isFreeFlow()
                  ? `Fill ${missing} more ${missing === 1 ? 'field' : 'fields'} to send for approval`
                  : 'Send for approval and finish setup'
            }
            onAction={() => void submit()}
            disabled={submitting || (missing > 0 && !isFreeFlow())}
            loading={submitting}
            note={`${filled} of ${FIELDS.length} fields done. Texts switch on by themselves once the carriers approve, usually 3 to 5 days.`}
            secondary={{ label: 'Do this later, start calling now', onClick: finish }}
            testId="send-business-details"
         />
      </div>
   )
}
